/**
 * @Relay no-runtime
 */
/**
 * @graphql agg
 */
import graphql from 'graphql-tag';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useContext } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError';

import type {
	useHasBillingInformationIsAccountInvoiceableQuery as useHasBillingInformationIsAccountInvoiceableQuery$data,
	useHasBillingInformationIsAccountInvoiceableQueryVariables as useHasBillingInformationIsAccountInvoiceableQuery$variables,
} from './__types__/useHasBillingInformationIsAccountInvoiceableQuery';

export interface UseHasBillingInformationResponse {
	hasBillingInformation: boolean;
	loading: boolean;
	error: Error | ApolloErrorInterop | null;
}

export const useHasBillingInformation = (
	skip: boolean = false,
): UseHasBillingInformationResponse => {
	const { cloudId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const shouldSkip = skip || !isSiteAdmin || !cloudId;

	const {
		data,
		loading,
		error: queryError,
	} = useQuery<
		useHasBillingInformationIsAccountInvoiceableQuery$data,
		useHasBillingInformationIsAccountInvoiceableQuery$variables
	>(
		graphql`
			query useHasBillingInformationIsAccountInvoiceableQuery(
				$cloudId: ID!
				$hamsProductKey: String!
			) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: $hamsProductKey) {
						entitlement {
							subscription {
								accountDetails {
									invoiceGroup {
										invoiceable
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { cloudId, hamsProductKey: 'confluence.ondemand' },
			skip: shouldSkip,
			client: getAGGClient(),
		},
	);

	if (shouldSkip) {
		return {
			hasBillingInformation: false,
			loading: false,
			error: null,
		};
	}
	let hasBillingInformation = false;
	let error = queryError || null;

	if (!loading) {
		if (queryError) {
			// unauthorized, not found, too many requests errors are expected
			if (
				isUnauthorizedError(queryError) ||
				isNotFoundError(queryError) ||
				isTooManyRequestsError(queryError) ||
				isGraphQLTooManyRequestsError(queryError)
			) {
				markErrorAsHandled(queryError);
				error = null;
			}
		} else {
			hasBillingInformation =
				data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails
					?.invoiceGroup?.invoiceable ?? false;
		}
	}

	return {
		hasBillingInformation,
		loading,
		error,
	};
};

/**
 * Checks if the provided error is a GraphQL error indicating a "429 Too Many Requests" scenario.
 *
 * In GraphQL, it's common for the HTTP response status to be 200 OK even when there are
 * application-level errors. This function helps identify "Too Many Requests" errors that
 * might be returned from a downstream service but are wrapped in a GraphQL response.
 *
 * @param error - The error object to inspect. It could be an ApolloErrorInterop or a generic error.
 * @returns {boolean} - Returns true if the error message contains an indication of a "429 Too Many Requests" error.
 */
const isGraphQLTooManyRequestsError = (error: ApolloErrorInterop | Error | any): boolean => {
	if (error && error.message) {
		return error.message.includes('GraphQL error: [429 Too Many Requests]');
	}
	return false;
};
